import {S} from './strings.mjs';
import {syncStep} from './live-sync.mjs';
const hex = b => [...b].map(v => v.toString(16).padStart(2, '0')).join('');
export const STATES = ['free', 'occupied', 'out_of_service', 'unknown'];
export function occupancyHeader(buffer) {
  const b = new Uint8Array(buffer), v = new DataView(buffer);
  if (b.length < 33 || String.fromCharCode(...b.slice(0, 4)) !== 'W2CS' || b[4] !== 1) throw Error('invalid occupancy');
  const count = v.getUint32(29, true);
  if (b.length !== 33 + Math.ceil(count / 4)) throw Error('incomplete occupancy');
  return {version: hex(b.slice(5, 13)), seq: Number(v.getBigUint64(13, true)), timestamp: Number(v.getBigUint64(21, true)), count};
}
export function occupancyStates(buffer, header = occupancyHeader(buffer)) {
  const b = new Uint8Array(buffer), states = new Uint8Array(header.count);
  for (let i = 0; i < header.count; i++) states[i] = (b[33 + (i >> 2)] >> ((i % 4) * 2)) & 3;
  return states;
}
export function decodeOccupancy(buffer) {
  const header = occupancyHeader(buffer);
  return {...header, states: Array.from(occupancyStates(buffer, header))};
}
// version(8) seq(8) then varints of index * radix + value: status events use radix 4, price events radix 256.
function decodeChanges(data, radix) {
  const b = Uint8Array.from(atob(data), c => c.charCodeAt(0));
  if (b.length < 16) throw Error('incomplete change');
  const updates = []; let pos = 16;
  while (pos < b.length) {
    let n = 0, factor = 1, done = false;
    for (let i = 0; i < 6; i++) {
      if (pos >= b.length) throw Error('incomplete change');
      const v = b[pos++]; n += (v & 127) * factor;
      if (!(v & 128)) { done = true; break; } factor *= 128;
    }
    if (!done) throw Error('invalid change');
    updates.push([Math.floor(n / radix), n % radix]);
  }
  return {version: hex(b.slice(0, 8)), seq: Number(new DataView(b.buffer).getBigUint64(8, true)), updates};
}
export const decodeDelta = data => decodeChanges(data, 4);
export const decodePriceDelta = data => decodeChanges(data, 256);

const BUFFER_LIMIT = 600, CATALOG_CHANGED = new Error('catalog change');
// Keeps the browser's copy of the catalog current. Protocol: w2c/live.py (events, ids, replay), states: web/live-sync.mjs.
// onChange(points) after a full load; onChange(points, updates, 'status' | 'prices') for every applied event or resync diff.
// codec (the map worker) decodes the catalog off the main thread; prices(points) returns the server's price table
// (Uint8Array of cents) when this catalog's prices were loaded, else null.
export function startLive(onCatalog, onChange, message, {enabled = true, onState = () => {}, timers = globalThis, random = Math.random,
  codec = null, prices = null, now = () => Date.now(), idleMs = 30000} = {}) {
  const local = codec || {
    async snapshot(catalogBuffer, occupancyBuffer) {
      const points = await onCatalog(catalogBuffer), header = occupancyHeader(occupancyBuffer), states = occupancyStates(occupancyBuffer, header);
      points.flatMap(p => p.points).forEach((p, i) => { p.status = STATES[states[i]]; });
      points.seq = header.seq; return points;
    },
    async occupancy(buffer) { const header = occupancyHeader(buffer); return {...header, states: occupancyStates(buffer, header)}; },
  };
  let stream, generation = 0, retry, unavailableTimer, watchdog, loadTimer, idleTimer, controller, buffer = null;
  let stopped = false, points, flat, owners, version, seq, failures = 0, resyncs = 0, lastInput = now(), reported, connected = false;
  const machine = {state: 'booting', ctx: {loaded: false, stale: false, enabled, hidden: false, offline: false, returning: false}};
  const reportState = value => { if (reported !== value) { reported = value; onState(value); } };
  const clearNotice = () => { timers.clearTimeout(unavailableTimer); unavailableTimer = undefined; };
  const unavailable = () => {
    if (unavailableTimer === undefined && enabled && !stopped && !document.hidden) unavailableTimer = timers.setTimeout(() => {
      // Keep the timer latched until recovery, so repeated failures cannot repeat the toast.
      if (enabled && !stopped && !document.hidden) { reportState('reconnecting'); message(S.unavailable); }
    }, 10000);
  };
  // The stream is proven alive (opened, heartbeat, event). Only a stream with data behind it counts as 'open'.
  const alive = () => {
    connected = true; clearNotice(); failures = 0; if (machine.state === 'live' || machine.state === 'resyncing') reportState('open');
    const token = generation; timers.clearTimeout(watchdog);
    watchdog = timers.setTimeout(() => fail(token), 40000);
  };
  // Heartbeats and applied events also prove that the resync loop is over.
  const steady = () => { resyncs = 0; alive(); };
  function close() {
    generation++; stream?.close(); stream = null; controller?.abort(); controller = null; buffer = null;
    for (const id of [retry, watchdog, loadTimer, idleTimer]) timers.clearTimeout(id);
  }
  const fail = token => { if (token === generation) dispatch('FAIL'); };
  const url = () => `/api/live?version=${version}&seq=${seq}&p=2`;

  // Every event is applied in arrival order. A seq other than the next one means events were lost: catch up.
  function applyEvent(kind, data) {
    let d;
    try { d = kind === 'prices' ? decodePriceDelta(data) : decodeDelta(data); } catch { dispatch('GAP'); return; }
    if (d.version === version && d.seq <= seq) return; // replay overlapping a snapshot
    if (d.version !== version || d.seq !== seq + 1 || d.updates.some(([i]) => i >= flat.length)) { dispatch('GAP'); return; }
    seq = d.seq;
    if (kind === 'prices') applyPrices(d.updates);
    else {
      for (const [i, state] of d.updates) flat[i].status = STATES[state];
      onChange(points, d.updates, 'status');
    }
    steady();
  }
  function applyPrices(updates) {
    if (points.prices !== points.version) return; // no table loaded: the next load of it is current
    if (!owners) { owners = new Array(flat.length); for (const site of points) for (const p of site.points) owners[p.index] = site; }
    for (const [i, cents] of updates) { flat[i].ct = cents; owners[i].secondaryVersion = (owners[i].secondaryVersion || 0) + 1; }
    onChange(points, updates, 'prices');
  }
  function openStream(token) {
    if (!enabled || token !== generation) return;
    stream?.close();
    const source = stream = new EventSource(url()); connected = false;
    const live = handler => event => { if (token === generation && stream === source) handler(event); };
    const feed = kind => live(event => {
      if (!buffer) return applyEvent(kind, event.data);
      if (buffer.events.length >= BUFFER_LIMIT) buffer.overflow = true; else buffer.events.push([kind, event.data]);
    });
    source.onopen = live(() => { alive(); if (machine.state === 'resyncing' && !buffer) dispatch('SYNCED'); });
    source.addEventListener('heartbeat', live(steady));
    source.addEventListener('delta', feed('delta'));
    source.addEventListener('prices', feed('prices'));
    // While the catalog is still being installed 'resync' only means: status needs a refresh afterwards.
    const resync = live(() => {
      if (!buffer) return dispatch('RESYNC');
      buffer.overflow = true; source.close(); stream = null;
    });
    source.addEventListener('resync', resync);
    source.addEventListener('snapshot', resync); // a server without protocol 2 (no replay) says this when a delta slipped through
    source.addEventListener('catalog', live(() => dispatch('CATALOG')));
    source.onerror = live(() => dispatch('FAIL'));
    // Also bound connections that never reach onopen.
    timers.clearTimeout(watchdog); watchdog = timers.setTimeout(() => fail(token), 40000);
  }
  const fetchBuffer = async (path, options, signal) => {
    const response = await fetch(path, {...options, signal});
    if (!response.ok) throw Error(path);
    return response.arrayBuffer();
  };
  const begin = () => {
    close();
    const token = generation; controller = new AbortController();
    // A stalled HTTP load is a real failure.
    loadTimer = timers.setTimeout(() => fail(token), 60000);
    return {token, signal: controller.signal};
  };
  const wrap = token => error => { if (token === generation) dispatch(error === CATALOG_CHANGED ? 'CATALOG' : 'FAIL'); };

  // Full load. The stream opens as soon as the occupancy header names version and seq; events wait until the
  // catalog is installed. sites.bin is needed once per visit and again only after a catalog change.
  async function load() {
    const {token, signal} = begin();
    const queue = buffer = {events: [], overflow: false};
    try {
      let header;
      const occupancyRequest = fetchBuffer('/api/occupancy.bin', {cache: 'no-store'}, signal).then(occupancyBuffer => {
        if (token !== generation) return occupancyBuffer;
        header = occupancyHeader(occupancyBuffer);
        version = header.version; seq = header.seq;
        openStream(token);
        return occupancyBuffer;
      });
      const [catalogBuffer, occupancyBuffer] = await Promise.all([fetchBuffer('/api/sites.bin', {cache: 'no-cache'}, signal), occupancyRequest]);
      if (token !== generation) return;
      const next = await local.snapshot(catalogBuffer, occupancyBuffer);
      if (token !== generation) return;
      const nextFlat = next.flatMap(p => p.points);
      if (header.version !== next.version || header.seq !== next.seq || header.count !== nextFlat.length) throw CATALOG_CHANGED;
      points = next; flat = nextFlat; owners = null;
      timers.clearTimeout(loadTimer); controller = null; resyncs = 0;
      onChange(points);
      dispatch('LOADED');
      if (token !== generation) return;
      buffer = null; if (connected) alive();
      for (const [kind, data] of queue.events) { if (token !== generation) return; applyEvent(kind, data); }
      if (queue.overflow) dispatch('GAP');
    } catch (error) { wrap(token)(error); }
  }
  // Same catalog, status behind: only occupancy.bin, the differences applied like events, then the stream from there.
  async function refresh() {
    if (++resyncs > 4) { resyncs = 0; dispatch('FAIL'); return; }
    const {token, signal} = begin();
    try {
      const occupancyBuffer = await fetchBuffer('/api/occupancy.bin', {cache: 'no-store'}, signal);
      if (token !== generation) return;
      const header = occupancyHeader(occupancyBuffer);
      if (header.version !== version || header.count !== flat.length) throw CATALOG_CHANGED;
      // A failing price table must not keep the status from catching up.
      const priceTable = (async () => prices?.(points))().catch(() => null);
      const [occupancy, cents] = await Promise.all([local.occupancy(occupancyBuffer), priceTable]);
      if (token !== generation) return;
      const statusChanges = [];
      for (let i = 0; i < flat.length; i++) { const state = occupancy.states[i]; if (flat[i].status !== STATES[state]) statusChanges.push([i, state]); }
      seq = header.seq;
      for (const [i, state] of statusChanges) flat[i].status = STATES[state];
      if (statusChanges.length) onChange(points, statusChanges, 'status');
      if (cents && cents.length === flat.length && points.prices === points.version) {
        const priceChanges = [];
        for (let i = 0; i < flat.length; i++) if (flat[i].ct !== cents[i]) priceChanges.push([i, cents[i]]);
        if (priceChanges.length) applyPrices(priceChanges);
      }
      timers.clearTimeout(loadTimer); controller = null;
      openStream(token);
      dispatch('SYNCED');
    } catch (error) { wrap(token)(error); }
  }
  // The stream alone: the server replays what happened since seq, or answers 'resync' / 'catalog'.
  function reconnect() {
    const {token} = begin();
    timers.clearTimeout(loadTimer);
    openStream(token);
  }
  function armIdle() {
    timers.clearTimeout(idleTimer);
    idleTimer = timers.setTimeout(() => {
      if (machine.state !== 'stale') return;
      if (now() - lastInput < idleMs) return armIdle();
      // Idle: nothing is being dragged or typed, and the page has a quiet moment for the install.
      if (globalThis.requestIdleCallback) globalThis.requestIdleCallback(() => dispatch('IDLE'), {timeout: 5000}); else dispatch('IDLE');
    }, Math.max(1000, idleMs - (now() - lastInput)));
  }
  const EFFECTS = {
    load, refresh, reconnect, close,
    'ensure-stream': () => { if (!stream) openStream(generation); },
    'arm-idle': () => { reportState('reconnecting'); armIdle(); },
    retry: () => {
      unavailable();
      if (document.hidden || globalThis.navigator?.onLine === false) return;
      const delay = Math.min(30000, 1500 * 2 ** Math.min(failures++, 5)) * (0.8 + random() * 0.2);
      retry = timers.setTimeout(() => dispatch('RETRY'), delay);
    },
  };
  function dispatch(event) {
    if (stopped) return;
    const ctx = machine.ctx;
    ctx.enabled = enabled; ctx.hidden = !!document.hidden; ctx.offline = globalThis.navigator?.onLine === false;
    const step = syncStep(machine.state, event, ctx);
    machine.state = step.state; Object.assign(ctx, step.ctx);
    if (step.state === 'hidden') clearNotice();
    if (step.state === 'off') { clearNotice(); reportState('off'); }
    if (event === 'OFFLINE' && step.state === 'offline') unavailable();
    for (const effect of step.effects) EFFECTS[effect]();
  }
  const input = () => { lastInput = now(); };
  const inputEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
  const visibility = () => dispatch(document.hidden ? 'HIDE' : 'SHOW');
  const offline = () => dispatch('OFFLINE');
  const online = () => { failures = 0; dispatch('ONLINE'); };
  document.addEventListener('visibilitychange', visibility);
  for (const name of inputEvents) document.addEventListener(name, input, {capture: true, passive: true});
  globalThis.addEventListener?.('offline', offline); globalThis.addEventListener?.('online', online);
  dispatch('START');
  const stop = () => {
    stopped = true; clearNotice(); close();
    document.removeEventListener('visibilitychange', visibility);
    for (const name of inputEvents) document.removeEventListener(name, input, {capture: true});
    globalThis.removeEventListener?.('offline', offline); globalThis.removeEventListener?.('online', online);
  };
  stop.setEnabled = value => {
    if (enabled === value) return;
    enabled = value; clearNotice();
    dispatch(value ? 'ENABLE' : 'DISABLE');
  };
  stop.state = () => machine.state;
  return stop;
}
